declare global {
  interface Window {
    posthog?: any;
  }
}

type AnalyticsUser = {
  id: string;
  email?: string | null;
  companyId?: number | null;
  authType?: string | null;
  isActive?: boolean | null;
};

const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_ENABLED = import.meta.env.VITE_POSTHOG_ENABLED !== "false";

let initialized = false;

function canUseAnalytics() {
  return typeof window !== "undefined" && import.meta.env.PROD && POSTHOG_ENABLED && !!POSTHOG_KEY;
}

function getPostHog() {
  if (!canUseAnalytics()) return null;
  return window.posthog || null;
}

export function initAnalytics(): void {
  if (!canUseAnalytics() || initialized) {
    return;
  }

  // PostHog snippet-style bootstrap to queue calls before the SDK finishes loading.
  ((documentRef, posthogRef) => {
    if (posthogRef?.__SV) {
      return;
    }
    let scriptEl: HTMLScriptElement;
    let firstScript: HTMLScriptElement | null;

    (window as any).posthog = posthogRef;
    posthogRef._i = [];
    posthogRef.init = function init(...args: any[]) {
      function posthogFactory(methodName: string) {
        return (...factoryArgs: any[]) => {
          const methodArgs = [methodName, ...factoryArgs];
          posthogRef.push(methodArgs);
        };
      }

      const posthogMethods = [
        "capture",
        "identify",
        "alias",
        "people.set",
        "people.set_once",
        "people.unset",
        "people.increment",
        "people.append",
        "reset",
        "set_config",
        "startSessionRecording",
        "stopSessionRecording",
      ];

      for (const method of posthogMethods) {
        const parts = method.split(".");
        if (parts.length === 2) {
          posthogRef[parts[0]] = posthogRef[parts[0]] || {};
          posthogRef[parts[0]][parts[1]] = posthogFactory(method);
        } else {
          posthogRef[parts[0]] = posthogFactory(method);
        }
      }

      posthogRef._i.push(args);
    };

    scriptEl = documentRef.createElement("script");
    scriptEl.type = "text/javascript";
    scriptEl.async = true;
    scriptEl.src = `${POSTHOG_HOST.replace(/\/$/, "")}/static/array.js`;
    firstScript = documentRef.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(scriptEl, firstScript);
    } else {
      documentRef.head.appendChild(scriptEl);
    }
  })(document, window.posthog || []);

  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    capture_pageleave: true,
    disable_surveys: true,
    disable_session_recording: false,
    session_recording: {
      sampleRate: 100,
      maskAllInputs: true,
    },
  });

  window.posthog.startSessionRecording?.();
  initialized = true;
}

export function identifyUser(user: AnalyticsUser): void {
  const posthog = getPostHog();
  if (!posthog || !user?.id) return;

  posthog.identify(user.id, {
    email: user.email ?? undefined,
    companyId: user.companyId ?? undefined,
    authType: user.authType ?? undefined,
    isActive: user.isActive ?? undefined,
  });
}

export function resetAnalyticsUser(): void {
  const posthog = getPostHog();
  if (!posthog) return;
  posthog.reset();
}

export function track(event: string, properties?: Record<string, unknown>): void {
  const posthog = getPostHog();
  if (!posthog) return;
  posthog.capture(event, properties);
}

export function trackPageview(path: string, properties?: Record<string, unknown>): void {
  track("$pageview", {
    path,
    current_url: typeof window !== "undefined" ? window.location.href : undefined,
    ...properties,
  });
}
